/** Shared refresh bus: one visible-page timer, with no employee data in tab events. */
export function createAttendanceSync({
  window: win = typeof window !== 'undefined' ? window : null,
  document: doc = typeof document !== 'undefined' ? document : null,
  intervalMs = 30_000,
  timers = globalThis
} = {}) {
  const storageKey = 'hr.attendance.changed.v1';
  const subscribers = new Set();
  let interval = null;
  let queued = null;
  let queuedReason = null;
  const visible = () => !doc || doc.visibilityState !== 'hidden';

  function enabled(entry) {
    try { return !entry.disposed && visible() && entry.enabled(); }
    catch { return false; }
  }

  function run(entry, reason) {
    if (!enabled(entry)) return Promise.resolve();
    if (entry.running) {
      // Background polls should not keep a slow API perpetually busy.
      if (reason !== 'poll') entry.again = reason;
      return entry.running;
    }
    entry.running = Promise.resolve()
      .then(() => enabled(entry) ? entry.callback({ reason }) : undefined)
      .catch(() => { /* Each consumer retains its existing error/retry UI. */ })
      .finally(() => {
        entry.running = null;
        const next = entry.again;
        entry.again = null;
        if (next && enabled(entry)) void run(entry, next);
      });
    return entry.running;
  }

  const refresh = (reason = 'mutation') => Promise.all([...subscribers].map((entry) => run(entry, reason)));
  function schedule(reason) {
    queuedReason = reason === 'mutation' ? 'mutation' : (queuedReason || reason);
    if (queued !== null) return;
    queued = timers.setTimeout(() => {
      const next = queuedReason;
      queued = queuedReason = null;
      void refresh(next);
    }, 80);
  }
  const onFocus = () => schedule('focus');
  const onVisibility = () => { if (visible()) schedule('resume'); };
  const onStorage = (event) => { if (event.key === storageKey && event.newValue) schedule('mutation'); };

  function start() {
    if (!win || interval !== null) return;
    interval = timers.setInterval(() => { if (visible()) void refresh('poll'); }, intervalMs);
    win.addEventListener('focus', onFocus);
    win.addEventListener('storage', onStorage);
    doc?.addEventListener('visibilitychange', onVisibility);
  }
  function stop() {
    if (interval !== null) timers.clearInterval(interval);
    if (queued !== null) timers.clearTimeout(queued);
    interval = queued = queuedReason = null;
    win?.removeEventListener('focus', onFocus);
    win?.removeEventListener('storage', onStorage);
    doc?.removeEventListener('visibilitychange', onVisibility);
  }

  function subscribe(callback, { enabled: isEnabled = () => true } = {}) {
    const entry = { callback, enabled: isEnabled, running: null, again: null, disposed: false };
    subscribers.add(entry);
    start();
    return {
      refresh: () => run(entry, 'resume'),
      stop: () => {
        entry.disposed = true;
        entry.again = null;
        subscribers.delete(entry);
        if (!subscribers.size) stop();
      }
    };
  }

  function notify() {
    schedule('mutation');
    try {
      // A nonce invalidates other tabs without storing IDs, reasons or HR data.
      win?.localStorage.setItem(storageKey, `${Date.now()}:${Math.random()}`);
    } catch { /* Private browsing can deny storage; same-tab refresh still works. */ }
  }
  return { subscribe, refresh, notify };
}

const attendanceSync = createAttendanceSync();
export const subscribeAttendanceRefresh = (callback, options) => attendanceSync.subscribe(callback, options);
export const notifyAttendanceChanged = () => attendanceSync.notify();
